/**
 * Enhanced Database Administration Dashboard
 * Manages certificates and institutions with Supabase integration
 * Shows students who have uploaded certificates from selected institutions
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabaseClient } from '../services/supabaseClient';
import { geminiOCR } from '../services/geminiOCR';
import './DatabaseAdmin.css';

// Import institutions data for population
const predefinedInstitutions = [
  // IITs
  { id: 'iit_bombay', name: 'Indian Institute of Technology Bombay', website: 'https://www.iitb.ac.in' },
  { id: 'iit_delhi', name: 'Indian Institute of Technology Delhi', website: 'https://www.iitd.ac.in' },
  { id: 'iit_madras', name: 'Indian Institute of Technology Madras', website: 'https://www.iitm.ac.in' },
  { id: 'iit_kanpur', name: 'Indian Institute of Technology Kanpur', website: 'https://www.iitk.ac.in' },
  { id: 'iit_kharagpur', name: 'Indian Institute of Technology Kharagpur', website: 'https://www.iitkgp.ac.in' },
  
  // NITs
  { id: 'nit_trichy', name: 'National Institute of Technology Tiruchirappalli', website: 'https://www.nitt.edu' },
  { id: 'nit_warangal', name: 'National Institute of Technology Warangal', website: 'https://www.nitw.ac.in' },
  { id: 'nit_surathkal', name: 'National Institute of Technology Karnataka', website: 'https://www.nitk.ac.in' },
  { id: 'nit_rourkela', name: 'National Institute of Technology Rourkela', website: 'https://www.nitrkl.ac.in' },
  { id: 'nit_jamshedpur', name: 'National Institute of Technology Jamshedpur', website: 'https://www.nitjsr.ac.in' },
  
  // Jharkhand Universities & Colleges
  { id: 'ranchi_university', name: 'Ranchi University - State University (Public)', website: 'https://www.ranchiuniversity.ac.in' },
  { id: 'sido_kanhu_murmu_university', name: 'Sido Kanhu Murmu University - State University (Public)', website: 'https://www.skmu.ac.in' },
  { id: 'bit_mesra', name: 'BIT Mesra - Deemed University (Private)', website: 'https://www.bitmesra.ac.in' },
  { id: 'iit_dhanbad', name: 'Indian Institute of Technology Dhanbad - IIT (Public)', website: 'https://www.iitism.ac.in' },
  { id: 'st_xaviers_ranchi', name: "St. Xavier's College Ranchi - Autonomous College (Private)", website: 'https://www.sxcran.org' },
  { id: 'marwari_college_ranchi', name: 'Marwari College Ranchi - Affiliated College (Private)', website: 'https://www.marwaricollegeranchi.org' },
  { id: 'gossner_college_ranchi', name: 'Gossner College Ranchi - Affiliated College (Private)', website: 'https://www.gossnercollege.ac.in' },
  { id: 'dav_college_jamshedpur', name: 'DAV College Jamshedpur - Affiliated College (Private)', website: 'https://www.davjamshedpur.com' },
  { id: 'vinoba_bhave_university', name: 'Vinoba Bhave University - State University (Public)', website: 'https://www.vbu.ac.in' },
  { id: 'kolhan_university', name: 'Kolhan University - State University (Public)', website: 'https://www.kolhanuniversity.ac.in' },
  { id: 'nilamber_pitamber_university', name: 'Nilamber-Pitamber University - State University (Public)', website: 'https://www.npujharkhand.ac.in' },
  { id: 'jamshedpur_cooperative_college', name: 'Jamshedpur Cooperative College - Affiliated College (Private)', website: '' },
  { id: 'karim_city_college', name: 'Karim City College Jamshedpur - Affiliated College (Private)', website: '' },
  { id: 'dumka_medical_college', name: 'Dumka Medical College - Government Medical College (Public)', website: '' },
  { id: 'rajendra_medical_college', name: 'Rajendra Institute of Medical Sciences - Government Medical College (Public)', website: 'https://www.rims.gov.in' },
  { id: 'birla_institute_technology_patna', name: 'Birla Institute of Technology Patna - Deemed University (Private)', website: 'https://www.bitmesra.ac.in' },
  { id: 'patna_university', name: 'Patna University - State University (Public)', website: 'https://www.patnauniversity.ac.in' },
  { id: 'magadh_university', name: 'Magadh University - State University (Public)', website: 'https://www.magadhuniversity.ac.in' }
];

const DatabaseAdmin = () => {
  const navigate = useNavigate();
  const [certificates, setCertificates] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [students, setStudents] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedInstitution, setSelectedInstitution] = useState('');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalCertificates: 0,
    totalInstitutions: 0,
    totalStudents: 0,
    recentCertificates: 0
  });
  const [activeTab, setActiveTab] = useState('overview');
  const [connectionStatus, setConnectionStatus] = useState({
    supabase: false,
    gemini: false
  });
  const [showAddInstitution, setShowAddInstitution] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [newInstitution, setNewInstitution] = useState({
    name: '',
    website: ''
  });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadInstitution, setUploadInstitution] = useState('');
  const [uploadProgress, setUploadProgress] = useState(null);

  useEffect(() => {
    checkConnections();
    loadInitialData();
  }, []);

  const checkConnections = async () => {
    setLoading(true);
    try {
      console.log('🔍 Checking Supabase connection...');
      const supabaseStatus = await supabaseClient.testConnection();
      console.log('Supabase status:', supabaseStatus);
      
      console.log('🔍 Checking Gemini OCR connection...');
      const geminiStatus = await geminiOCR.testConnection();
      console.log('Gemini status:', geminiStatus);
      
      setConnectionStatus({
        supabase: supabaseStatus,
        gemini: geminiStatus.success
      });
      
      if (!supabaseStatus) {
        console.warn('⚠️ Supabase connection failed. Check:');
        console.warn('1. Environment variables are set correctly');
        console.warn('2. Database tables exist (run setup-certificates.sql)');
        console.warn('3. Network connection is working');
      }
    } catch (error) {
      console.error('❌ Connection check failed:', error);
      setConnectionStatus({
        supabase: false,
        gemini: false
      });
    }
    setLoading(false);
  };

  const loadInitialData = async () => {
    setLoading(true);
    try {
      console.log('Loading initial data...');
      const [certificatesData, institutionsData] = await Promise.all([
        supabaseClient.getRecentCertificates(50),
        supabaseClient.getInstitutions()
      ]);

      console.log('Certificates loaded:', certificatesData?.length || 0);
      console.log('Institutions loaded:', institutionsData?.length || 0);

      setCertificates(certificatesData || []);
      setInstitutions(institutionsData || []);

      // Extract unique students from certificates
      const uniqueStudents = extractUniqueStudents(certificatesData || []);
      setStudents(uniqueStudents);

      // Calculate stats
      setStats({
        totalCertificates: (certificatesData || []).length,
        totalInstitutions: (institutionsData || []).length,
        totalStudents: uniqueStudents.length,
        recentCertificates: (certificatesData || []).filter(cert => 
          new Date(cert.upload_date) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
        ).length
      });
    } catch (error) {
      console.error('Failed to load data:', error);
      // Set empty arrays to avoid undefined states
      setCertificates([]);
      setInstitutions([]);
      setStudents([]);
      setStats({
        totalCertificates: 0,
        totalInstitutions: 0,
        totalStudents: 0,
        recentCertificates: 0
      });
    }
    setLoading(false);
  };

  const extractUniqueStudents = (certificatesData) => {
    const studentMap = new Map();
    
    certificatesData.forEach(cert => {
      if (cert.student_name) {
        const key = `${cert.student_name}-${cert.institution_id || cert.institution}`;
        if (!studentMap.has(key)) {
          studentMap.set(key, {
            id: key,
            name: cert.student_name,
            rollNumber: cert.roll_number,
            institution: cert.institutions?.name || cert.institution,
            institutionId: cert.institution_id,
            program: cert.program,
            batch: cert.batch,
            totalCertificates: 1,
            lastUpload: cert.upload_date,
            certificates: [cert]
          });
        } else {
          const student = studentMap.get(key);
          student.totalCertificates += 1;
          student.certificates.push(cert);
          if (new Date(cert.upload_date) > new Date(student.lastUpload)) {
            student.lastUpload = cert.upload_date;
          }
        }
      }
    });

    return Array.from(studentMap.values());
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      await loadInitialData();
      return;
    }

    setLoading(true);
    try {
      const results = await supabaseClient.searchCertificates(searchQuery);
      setCertificates(results);
      setStudents(extractUniqueStudents(results));
    } catch (error) {
      console.error('Search failed:', error);
    }
    setLoading(false);
  };

  const filterByInstitution = async (institutionId) => {
    setSelectedInstitution(institutionId);
    
    if (!institutionId) {
      await loadInitialData();
      return;
    }

    setLoading(true);
    try {
      const results = await supabaseClient.getCertificatesByInstitution(institutionId);
      setCertificates(results);
      setStudents(extractUniqueStudents(results));
    } catch (error) {
      console.error('Filter failed:', error);
    }
    setLoading(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getAuthenticationStatus = (authResult) => {
    if (!authResult) return { status: 'pending', color: '#f39c12', text: 'Pending' };
    
    if (authResult.verified === true) {
      return { status: 'verified', color: '#27ae60', text: 'Verified' };
    } else if (authResult.verified === false) {
      return { status: 'rejected', color: '#e74c3c', text: 'Rejected' };
    }
    
    return { status: 'unknown', color: '#95a5a6', text: 'Unknown' };
  };

  // =================== INSTITUTION MANAGEMENT ===================
  
  const handleAddInstitution = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await supabaseClient.addInstitution(newInstitution);
      setNewInstitution({
        name: '',
        website: ''
      });
      setShowAddInstitution(false);
      await loadInitialData(); // Reload data
    } catch (error) {
      console.error('Failed to add institution:', error);
      alert('Failed to add institution. Please try again.');
    }
    setLoading(false);
  };

  const handleDeleteInstitution = async (institutionId) => {
    if (!window.confirm('Are you sure you want to delete this institution? This action cannot be undone.')) {
      return;
    }
    
    setLoading(true);
    try {
      await supabaseClient.deleteInstitution(institutionId);
      await loadInitialData(); // Reload data
    } catch (error) {
      console.error('Failed to delete institution:', error);
      alert('Failed to delete institution. It may have associated certificates.');
    }
    setLoading(false);
  };

  // =================== BULK STUDENT UPLOAD ===================
  
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'text/csv') {
      setUploadFile(file);
    } else {
      alert('Please select a CSV file');
    }
  };

  const parseCSV = (csvText) => {
    const lines = csvText.split('\n').filter(line => line.trim());
    if (lines.length < 2) return [];
    
    const headers = lines[0].split(',').map(h => h.trim());
    const students = [];
    
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      if (values.length >= headers.length) {
        const student = {};
        headers.forEach((header, index) => {
          student[header] = values[index];
        });
        students.push(student);
      }
    }
    
    return students;
  };

  const handleBulkUpload = async () => {
    if (!uploadFile || !uploadInstitution) {
      alert('Please select a CSV file and institution');
      return;
    }
    
    setUploadProgress({ status: 'reading', message: 'Reading CSV file...' });
    
    try {
      const csvText = await uploadFile.text();
      const students = parseCSV(csvText);
      
      if (students.length === 0) {
        alert('No valid student data found in CSV');
        setUploadProgress(null);
        return;
      }
      
      setUploadProgress({ 
        status: 'uploading', 
        message: `Uploading ${students.length} students...`,
        total: students.length,
        current: 0
      });
      
      await supabaseClient.addStudentsBulk(uploadInstitution, students);
      
      setUploadProgress({ 
        status: 'complete', 
        message: `Successfully uploaded ${students.length} students!` 
      });
      
      // Reset form
      setUploadFile(null);
      setUploadInstitution('');
      setShowBulkUpload(false);
      await loadInitialData();
      
      setTimeout(() => setUploadProgress(null), 3000);
    } catch (error) {
      console.error('Bulk upload failed:', error);
      setUploadProgress({ 
        status: 'error', 
        message: 'Upload failed: ' + error.message 
      });
      setTimeout(() => setUploadProgress(null), 5000);
    }
  };

  const downloadSampleCSV = () => {
    const sampleData = `firstName,lastName,rollNumber,program,batch,email,phone
John,Doe,CS2024001,Computer Science,2024-2028,john.doe@email.com,+91-9876543210
Jane,Smith,CS2024002,Computer Science,2024-2028,jane.smith@email.com,+91-9876543211`;
    
    const blob = new Blob([sampleData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample-students.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const populatePredefinedInstitutions = async () => {
    if (!window.confirm(`This will add ${predefinedInstitutions.length} predefined institutions to your database. Continue?`)) {
      return;
    }

    setLoading(true);
    setUploadProgress({ 
      status: 'uploading', 
      message: 'Adding predefined institutions...',
      total: predefinedInstitutions.length,
      current: 0
    });

    try {
      let successCount = 0;
      let errorCount = 0;

      for (let i = 0; i < predefinedInstitutions.length; i++) {
        const institution = predefinedInstitutions[i];
        setUploadProgress({ 
          status: 'uploading', 
          message: `Adding ${institution.name}...`,
          total: predefinedInstitutions.length,
          current: i + 1
        });

        try {
          await supabaseClient.addInstitution(institution);
          successCount++;
        } catch (error) {
          errorCount++;
          if (!error.message.includes('duplicate')) {
            console.error(`Failed to add ${institution.name}:`, error);
          }
        }
      }

      setUploadProgress({ 
        status: 'complete', 
        message: `✅ Successfully processed ${predefinedInstitutions.length} institutions! (${successCount} new, ${errorCount} existing)` 
      });

      await loadInitialData(); // Reload data
      setTimeout(() => setUploadProgress(null), 5000);

    } catch (error) {
      console.error('Population failed:', error);
      setUploadProgress({ 
        status: 'error', 
        message: 'Population failed: ' + error.message 
      });
      setTimeout(() => setUploadProgress(null), 5000);
    }
    setLoading(false);
  };

  const renderConnectionStatus = () => (
    <div className="connection-status">
      <h3>🔗 Service Status</h3>
      <div className="status-grid">
        <div className={`status-item ${connectionStatus.supabase ? 'connected' : 'disconnected'}`}>
          <span className="status-icon">{connectionStatus.supabase ? '✅' : '❌'}</span>
          <span>Supabase Database</span>
          {!connectionStatus.supabase && <small>Check your connection</small>}
        </div>
        <div className={`status-item ${connectionStatus.gemini ? 'connected' : 'disconnected'}`}>
          <span className="status-icon">{connectionStatus.gemini ? '✅' : '❌'}</span>
          <span>Gemini AI OCR</span>
          {!connectionStatus.gemini && <small>API may be down</small>}
        </div>
      </div>
      {(!connectionStatus.supabase || !connectionStatus.gemini) && (
        <div className="connection-warning">
          ⚠️ Some services are not available. This may affect data loading and functionality.
        </div>
      )}
    </div>
  );

  const renderStatsCards = () => (
    <div className="stats-cards">
      <div className="stat-card">
        <h3>📊 Total Certificates</h3>
        <div className="stat-number">{stats.totalCertificates}</div>
      </div>
      <div className="stat-card">
        <h3>🏛️ Institutions</h3>
        <div className="stat-number">{stats.totalInstitutions}</div>
      </div>
      <div className="stat-card">
        <h3>👥 Students</h3>
        <div className="stat-number">{stats.totalStudents}</div>
      </div>
      <div className="stat-card">
        <h3>📅 Recent (7 days)</h3>
        <div className="stat-number">{stats.recentCertificates}</div>
      </div>
    </div>
  );

  const renderSearchFilters = () => (
    <div className="search-filters">
      <div className="search-filters-grid">
        <div className="search-bar">
          <label>Search Certificates</label>
          <input
            type="text"
            placeholder="Search by student name, roll number, or certificate type..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          />
        </div>
        
        <div className="institution-filter">
          <label>Filter by Institution</label>
          <select 
            value={selectedInstitution}
            onChange={(e) => filterByInstitution(e.target.value)}
          >
            <option value="">All Institutions</option>
            {institutions.map(inst => (
              <option key={inst.id} value={inst.id}>{inst.name}</option>
            ))}
          </select>
        </div>

        <button 
          onClick={handleSearch} 
          disabled={loading}
          className="search-btn"
        >
          {loading ? '🔄' : '🔍'} Search
        </button>

        <button onClick={loadInitialData} className="refresh-btn">
          🔄 Refresh
        </button>
      </div>
    </div>
  );

  const renderStudentsTab = () => {
    const selectedInstitutionName = institutions.find(inst => inst.id === selectedInstitution)?.name;
    const filteredStudents = selectedInstitution 
      ? students.filter(student => student.institutionId === selectedInstitution)
      : students;

    return (
      <div className="students-tab">
        <div className="tab-header">
          <h3>👥 Students Who Have Uploaded Certificates</h3>
          {selectedInstitutionName && (
            <p className="institution-filter-info">
              Showing students from: <strong>{selectedInstitutionName}</strong>
            </p>
          )}
        </div>

        {filteredStudents.length === 0 ? (
          <div className="no-data">
            {selectedInstitution 
              ? `👥 No students found for ${selectedInstitutionName}. Students will appear here when they upload certificates.`
              : '👥 No students have uploaded certificates yet. Students will appear here when they upload certificates.'
            }
          </div>
        ) : (
          <div className="students-grid">
            {filteredStudents.map(student => (
              <div key={student.id} className="student-card">
                <div className="student-header">
                  <h4>{student.name}</h4>
                  <span className="certificate-count">
                    {student.totalCertificates} certificate{student.totalCertificates !== 1 ? 's' : ''}
                  </span>
                </div>
                
                <div className="student-details">
                  {student.rollNumber && (
                    <p><strong>Roll Number:</strong> {student.rollNumber}</p>
                  )}
                  {student.program && (
                    <p><strong>Program:</strong> {student.program}</p>
                  )}
                  {student.batch && (
                    <p><strong>Batch:</strong> {student.batch}</p>
                  )}
                  <p><strong>Institution:</strong> {student.institution}</p>
                  <p><strong>Last Upload:</strong> {formatDate(student.lastUpload)}</p>
                </div>

                <div className="student-certificates">
                  <h5>📋 Uploaded Certificates:</h5>
                  <ul>
                    {student.certificates.map(cert => (
                      <li key={cert.id}>
                        <span className="cert-type">{cert.certificate_type || 'Certificate'}</span>
                        <span className="cert-date">{formatDate(cert.upload_date)}</span>
                        {cert.ipfs_url && (
                          <button 
                            className="view-cert-btn"
                            onClick={() => window.open(cert.ipfs_url, '_blank')}
                            title="View certificate on IPFS"
                          >
                            🔗
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderCertificatesTab = () => (
    <div className="certificates-tab">
      {renderSearchFilters()}
      
      <div className="certificates-table">
        <table>
          <thead>
            <tr>
              <th>Student Name</th>
              <th>Institution</th>
              <th>Certificate Type</th>
              <th>Roll Number</th>
              <th>Upload Date</th>
              <th>Status</th>
              <th>OCR Method</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {certificates.map(cert => {
              const authStatus = getAuthenticationStatus(cert.authentication_result);
              const ocrMethod = cert.ocr_data?.extraction_metadata?.method || 'unknown';
              
              return (
                <tr key={cert.id}>
                  <td>{cert.student_name || 'N/A'}</td>
                  <td>{cert.institutions?.name || cert.institution || 'N/A'}</td>
                  <td>{cert.certificate_type || 'N/A'}</td>
                  <td>{cert.roll_number || 'N/A'}</td>
                  <td>{formatDate(cert.upload_date)}</td>
                  <td>
                    <span 
                      className="auth-status" 
                      style={{ color: authStatus.color }}
                    >
                      {authStatus.text}
                    </span>
                  </td>
                  <td>
                    <span className={`ocr-method ${ocrMethod}`}>
                      {ocrMethod.replace('_', ' ').toUpperCase()}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        onClick={() => window.open(cert.ipfs_url, '_blank')}
                        title="View on IPFS"
                      >
                        🔗
                      </button>
                      <button 
                        onClick={() => navigator.clipboard.writeText(cert.file_hash)}
                        title="Copy Hash"
                      >
                        📋
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        
        {certificates.length === 0 && !loading && (
          <div className="no-data">
            📄 No certificates found. 
            <p>Try one of the following:</p>
            <ul>
              <li>• Upload some certificates using the main application</li>
              <li>• Check if you're connected to the right database</li>
              <li>• Adjust your search criteria or clear filters</li>
            </ul>
            <button onClick={() => navigate('/')} className="go-upload-btn">
              📤 Go to Upload Page
            </button>
          </div>
        )}
      </div>
    </div>
  );

  const renderInstitutionsTab = () => (
    <div className="institutions-tab">
      <div className="institutions-header">
        <h3>🏛️ Registered Institutions</h3>
        <div className="institution-actions">
          <button 
            className="add-institution-btn"
            onClick={() => setShowAddInstitution(true)}
          >
            ➕ Add Institution
          </button>
          <button 
            className="bulk-upload-btn"
            onClick={() => setShowBulkUpload(true)}
          >
            📤 Bulk Upload Students
          </button>
          <button 
            className="populate-institutions-btn"
            onClick={populatePredefinedInstitutions}
            disabled={loading}
          >
            🏛️ Populate Common Institutions
          </button>
        </div>
      </div>

      {/* Institution Population Progress */}
      {uploadProgress && (
        <div className={`upload-progress ${uploadProgress.status}`}>
          <p>{uploadProgress.message}</p>
          {uploadProgress.total && (
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
              ></div>
            </div>
          )}
        </div>
      )}

      {/* Add Institution Modal */}
      {showAddInstitution && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>➕ Add New Institution</h3>
              <button 
                className="close-btn"
                onClick={() => setShowAddInstitution(false)}
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleAddInstitution}>
              <div className="form-group">
                <label>Institution Name *</label>
                <input
                  type="text"
                  value={newInstitution.name}
                  onChange={(e) => setNewInstitution({...newInstitution, name: e.target.value})}
                  required
                  placeholder="e.g., National Institute of Technology"
                />
              </div>
              <div className="form-group">
                <label>Website Link</label>
                <input
                  type="url"
                  value={newInstitution.website}
                  onChange={(e) => setNewInstitution({...newInstitution, website: e.target.value})}
                  placeholder="https://www.institution.edu"
                />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowAddInstitution(false)}>
                  Cancel
                </button>
                <button type="submit" disabled={loading}>
                  {loading ? 'Adding...' : 'Add Institution'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      {showBulkUpload && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>📤 Bulk Upload Students</h3>
              <button 
                className="close-btn"
                onClick={() => setShowBulkUpload(false)}
              >
                ✕
              </button>
            </div>
            <div className="bulk-upload-content">
              <div className="upload-instructions">
                <h4>📋 Instructions:</h4>
                <ol>
                  <li>Download the sample CSV file to see the required format</li>
                  <li>Fill in your student data following the same format</li>
                  <li>Select the institution these students belong to</li>
                  <li>Upload your CSV file</li>
                </ol>
                <button onClick={downloadSampleCSV} className="download-sample-btn">
                  📥 Download Sample CSV
                </button>
              </div>

              <div className="form-group">
                <label>Select Institution *</label>
                <select
                  value={uploadInstitution}
                  onChange={(e) => setUploadInstitution(e.target.value)}
                  required
                >
                  <option value="">-- Select Institution --</option>
                  {institutions.map(inst => (
                    <option key={inst.id} value={inst.id}>{inst.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>CSV File *</label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  required
                />
                {uploadFile && (
                  <p className="file-info">
                    Selected: {uploadFile.name} ({(uploadFile.size / 1024).toFixed(1)} KB)
                  </p>
                )}
              </div>

              {uploadProgress && (
                <div className={`upload-progress ${uploadProgress.status}`}>
                  <p>{uploadProgress.message}</p>
                  {uploadProgress.total && (
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                      ></div>
                    </div>
                  )}
                </div>
              )}

              <div className="modal-actions">
                <button onClick={() => setShowBulkUpload(false)}>
                  Cancel
                </button>
                <button 
                  onClick={handleBulkUpload}
                  disabled={!uploadFile || !uploadInstitution || loading}
                >
                  {loading ? 'Uploading...' : 'Upload Students'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="institutions-grid">
        {institutions.map(inst => {
          const institutionStudents = students.filter(student => student.institutionId === inst.id);
          const institutionCertificates = certificates.filter(cert => 
            cert.institution_id === inst.id || cert.institution === inst.name
          );
          
          return (
            <div key={inst.id} className="institution-card">
              <div className="institution-header">
                <h4>{inst.name}</h4>
                <button 
                  className="delete-institution-btn"
                  onClick={() => handleDeleteInstitution(inst.id)}
                  title="Delete Institution"
                >
                  🗑️
                </button>
              </div>
              <div className="institution-details">
                <p><strong>ID:</strong> {inst.id}</p>
                {inst.website_link && (
                  <p><strong>Website:</strong> 
                    <a href={inst.website_link} target="_blank" rel="noopener noreferrer">
                      {inst.website_link}
                    </a>
                  </p>
                )}
                <p><strong>Students:</strong> {institutionStudents.length}</p>
                <p><strong>Certificates:</strong> {institutionCertificates.length}</p>
                <p><strong>Registered:</strong> {formatDate(inst.created_at)}</p>
              </div>
              <div className="institution-actions">
                <button 
                  onClick={() => {
                    setSelectedInstitution(inst.id);
                    setActiveTab('students');
                    filterByInstitution(inst.id);
                  }}
                  className="view-students-btn"
                >
                  👥 View Students
                </button>
                <button 
                  onClick={() => {
                    setSelectedInstitution(inst.id);
                    setActiveTab('certificates');
                    filterByInstitution(inst.id);
                  }}
                  className="view-certificates-btn"
                >
                  📄 View Certificates
                </button>
              </div>
            </div>
          );
        })}
      </div>
      
      {institutions.length === 0 && !loading && (
        <div className="no-data">
          🏛️ No institutions registered yet.
          <p>Get started by adding your first institution!</p>
          <button 
            onClick={() => setShowAddInstitution(true)}
            className="add-first-institution-btn"
          >
            ➕ Add Your First Institution
          </button>
        </div>
      )}
    </div>
  );

  const renderOverviewTab = () => (
    <div className="overview-tab">
      {renderStatsCards()}
      
      <div className="recent-activity">
        <h3>📋 Recent Certificate Uploads</h3>
        {certificates.length === 0 ? (
          <div className="no-data">
            📄 No certificates uploaded yet.
            <p>Get started by uploading your first certificate!</p>
            <button onClick={() => navigate('/')} className="go-upload-btn">
              📤 Upload Certificates
            </button>
          </div>
        ) : (
          <div className="certificates-table">
            <table>
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Institution</th>
                  <th>Certificate Type</th>
                  <th>Upload Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {certificates.slice(0, 10).map(cert => {
                  const authStatus = getAuthenticationStatus(cert.authentication_result);
                  return (
                    <tr key={cert.id}>
                      <td>{cert.student_name || 'N/A'}</td>
                      <td>{cert.institutions?.name || cert.institution || 'N/A'}</td>
                      <td>{cert.certificate_type || 'N/A'}</td>
                      <td>{formatDate(cert.upload_date)}</td>
                      <td>
                        <span 
                          className="auth-status" 
                          style={{ color: authStatus.color }}
                        >
                          {authStatus.text}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="database-admin">
      <div className="admin-header">
        <div className="header-content">
          <div className="header-text">
            <h1> Database Administration</h1>
            <p>Manage certificates and institutions with Gemini AI integration</p>
          </div>
          <div className="header-actions">
            <button 
              className="refresh-all-btn"
              onClick={loadInitialData}
              disabled={loading}
              title="Refresh all data"
            >
              🔄 Refresh
            </button>
            <button 
              className="home-btn"
              onClick={() => navigate('/')}
              title="Go to Home Page"
            >
              🏠 Home
            </button>
            <button 
              className="logout-btn"
              onClick={() => {
                localStorage.removeItem('user');
                localStorage.removeItem('isAuthenticated');
                navigate('/login');
              }}
              title="Logout"
              style={{
                backgroundColor: '#ef4444',
                color: 'white',
                padding: '8px 16px',
                borderRadius: '6px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              🚪 Logout
            </button>
          </div>
        </div>
      </div>

      {renderConnectionStatus()}

      <div className="admin-tabs">
        <button 
          className={activeTab === 'overview' ? 'active' : ''}
          onClick={() => setActiveTab('overview')}
        >
          📊 Overview
        </button>
        <button 
          className={activeTab === 'students' ? 'active' : ''}
          onClick={() => setActiveTab('students')}
        >
          👥 Students
        </button>
        <button 
          className={activeTab === 'certificates' ? 'active' : ''}
          onClick={() => setActiveTab('certificates')}
        >
          📄 Certificates
        </button>
        <button 
          className={activeTab === 'institutions' ? 'active' : ''}
          onClick={() => setActiveTab('institutions')}
        >
          🏛️ Institutions
        </button>
      </div>

      <div className="tab-content">
        {loading && (
          <div className="loading">
            <div className="spinner"></div>
            <p>Loading certificates and verifying database connection...</p>
            <small>This may take a moment for large datasets</small>
          </div>
        )}
        
        {!loading && activeTab === 'overview' && renderOverviewTab()}
        {!loading && activeTab === 'students' && renderStudentsTab()}
        {!loading && activeTab === 'certificates' && renderCertificatesTab()}
        {!loading && activeTab === 'institutions' && renderInstitutionsTab()}
      </div>
    </div>
  );
};

export default DatabaseAdmin;